import { type CapturedContext, captureContext, captureScreenshot } from './capture';
import type { WidgetHost } from './host';
import type { Messages } from './i18n';
import { computeSelector } from './selector';
import { type FeedbackPayload, submitFeedback } from './transport';
import type { ResolvedOptions } from './types';
import { createDialog, type Dialog } from './ui/dialog';
import { createLauncher, type Launcher } from './ui/launcher';
import { createPicker, type Picker } from './ui/picker';

export type WidgetState = 'idle' | 'picking' | 'composing' | 'sending' | 'success' | 'error';

const SUCCESS_AUTO_CLOSE_MS = 2000;

export interface Controller {
  destroy(): void;
  /** Replaces the project default email; applies on the next dialog open */
  setDefaultEmail(email: string | undefined): void;
}

interface Selection {
  element: Element;
  context: CapturedContext;
}

export function createController(deps: {
  host: WidgetHost;
  options: ResolvedOptions;
  messages: Messages;
  reviewerKey: string;
  /** Project-configured default that pre-fills the dialog's email field */
  defaultEmail?: string | undefined;
}): Controller {
  const { host, options, messages } = deps;

  let state: WidgetState = 'idle';
  let selection: Selection | null = null;
  let successTimer: ReturnType<typeof setTimeout> | null = null;
  // Mutable: background revalidation may refresh it after mount (see index.ts).
  let defaultEmail = deps.defaultEmail;

  // Indirection around reading `state` on purpose: `setState(...)` is an opaque call
  // from TypeScript's point of view, so a direct `state === 'sending'` guard followed
  // later by another direct read gets over-narrowed by control-flow analysis (it
  // "proves" state can never be 'sending' at the later point, which is wrong — that's
  // the very thing these guards check for). Reading through a function call sidesteps it.
  function currentState(): WidgetState {
    return state;
  }

  const launcher: Launcher = createLauncher({
    root: host.root,
    messages,
    onToggle: () => {
      // A send in flight owns the dialog; Escape/toggle must not yank it away mid-request.
      if (currentState() === 'sending') return;
      if (state === 'idle') {
        setState('picking');
      } else {
        setState('idle');
      }
    },
  });

  const picker: Picker = createPicker({
    root: host.root,
    hostElement: host.element,
    messages,
    onSelect: handleSelect,
    onCancel: () => {
      if (currentState() === 'sending') return;
      setState('idle');
    },
  });

  const dialog: Dialog = createDialog({
    root: host.root,
    messages,
    showEmailField: !options.reporterEmail,
    onSubmit: (comment, dialogEmail) => {
      void handleSubmit(comment, dialogEmail);
    },
    onCancel: () => {
      // Dialog's Escape handler bypasses the (disabled) cancel button, so this
      // guard is the real gate keeping a send uncancelable once started.
      if (currentState() === 'sending') return;
      setState('idle');
    },
  });

  /**
   * Escape belongs to the widget whenever the widget is active — but host pages
   * claim it first: Radix and everything modelled on it dismiss their open layer
   * from `document.addEventListener('keydown', h, { capture: true })`, which runs
   * before any listener inside the widget's shadow tree. Pressing Escape to close
   * the widget would take the host's dialog down with it.
   *
   * `window` is the one node ahead of `document` in the capture path, so this is
   * the only place the event can be claimed first. Because it stops there, this
   * handler — not picker.ts or dialog.ts — is what actually runs the widget's
   * Escape while a widget is mounted; the handlers inside those two modules stay
   * as the meaning of Escape for each, and this routes to them.
   */
  function handleEscapeKey(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || currentState() === 'idle') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    // A send in flight owns the dialog (see onToggle); swallow the key regardless
    // so it can't reach the host page, but leave the dialog where it is.
    if (currentState() === 'sending') return;
    if (currentState() === 'picking') {
      setState('idle');
      return;
    }
    dialog.escape();
  }
  window.addEventListener('keydown', handleEscapeKey, true);

  function clearSuccessTimer(): void {
    if (successTimer !== null) {
      clearTimeout(successTimer);
      successTimer = null;
    }
  }

  function setState(next: WidgetState): void {
    if (state === next) return;

    if (state === 'picking' && next !== 'picking') {
      picker.stop();
    }
    if (next === 'idle') {
      dialog.close();
      selection = null;
      clearSuccessTimer();
    }

    state = next;
    launcher.setActive(next !== 'idle');

    if (next === 'picking') {
      picker.start();
    }
  }

  function handleSelect(element: Element): void {
    const selector = computeSelector(element);
    selection = { element, context: captureContext(element, selector) };
    setState('composing');
    // A config-level reporterEmail renders no email field at all — a prefill
    // would be pointless there; it only applies to the visible, editable field.
    dialog.open(
      element.getBoundingClientRect(),
      selector,
      options.reporterEmail ? undefined : defaultEmail,
    );
  }

  async function handleSubmit(comment: string, dialogEmail: string | null): Promise<void> {
    if (!selection || currentState() === 'sending') return;

    // Snapshot what this send is about: `selection`/the reviewer key must not be able
    // to shift under us while we're mid-flight across the awaits below.
    const snap = selection;
    const reviewerKey = deps.reviewerKey;

    setState('sending');
    dialog.setBusy(true);
    launcher.setDisabled(true);

    try {
      const screenshot = options.screenshot ? await captureScreenshot(snap.element) : null;
      // Bail silently if this send was superseded (e.g. the widget was torn down)
      // while the screenshot capture was in flight.
      if (currentState() !== 'sending') return;

      const payload: FeedbackPayload = {
        token: options.token,
        reviewerKey,
        comment,
        ...snap.context,
        ...(screenshot ? { screenshot } : {}),
        ...(options.metadata ? { metadata: options.metadata } : {}),
        reporterEmail: options.reporterEmail ?? dialogEmail ?? undefined,
        locale: options.locale,
      };

      const result = await submitFeedback(options.endpoint, payload);
      if (currentState() !== 'sending') return;

      if (result.ok) {
        setState('success');
        dialog.showSuccess();
        successTimer = setTimeout(() => setState('idle'), SUCCESS_AUTO_CLOSE_MS);
      } else {
        setState('error');
        dialog.showError();
      }
    } catch {
      if (currentState() === 'sending') {
        setState('error');
        dialog.showError();
      }
    } finally {
      launcher.setDisabled(false);
      // showSuccess()/showError() above already own the dialog's busy-adjacent UI;
      // only reset it here if we bailed out while the dialog was still composing
      // (nothing else has claimed responsibility for its controls).
      if (currentState() === 'composing') {
        dialog.setBusy(false);
      }
    }
  }

  return {
    destroy() {
      clearSuccessTimer();
      window.removeEventListener('keydown', handleEscapeKey, true);
      picker.destroy();
      dialog.destroy();
      launcher.destroy();
      host.destroy();
    },
    setDefaultEmail(email: string | undefined) {
      defaultEmail = email;
    },
  };
}
